import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/*  Cart Items Component  */

class CartItemsComponent extends Component {

  // Debounce quantity changes to avoid rapid requests
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  /* ================= LIFECYCLE ================= */

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }


  /* ================= QUANTITY CHANGE ================= */

  // Triggered when + / - button is clicked
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;
    if (!line) return;

    // Quantity 0 means remove item
    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    // Update quantity in cart
    this.updateQuantity({ line, quantity, action: 'change' });

    // Show loading shimmer
    const row = this.refs.cartItemRows[line - 1];
    row?.querySelector('text-component')?.shimmer();
  }


  /* ================= REMOVE ITEM ================= */

  // Remove item row visually
  onLineItemRemove(line) {
    this.updateQuantity({ line, quantity: 0, action: 'clear' });

    const row = this.refs.cartItemRows[line - 1];
    if (!row) return;

    // Remove main row + child rows
    const rowsToRemove = [
      row,
      ...this.refs.cartItemRows.filter(r => r.dataset.parentKey === row.dataset.key)
    ];

    rowsToRemove.forEach(r => {
      const remove = () => r.remove();

      if (prefersReducedMotion()) return remove();

      r.style.setProperty('--row-height', `${r.clientHeight}px`);
      r.classList.add('removing');
      onAnimationEnd(r, remove);
    });
  }


  /* ================= UPDATE CART ================= */

  // Send cart change request to Shopify
  updateQuantity({ line, quantity, action }) {
    const marker = cartPerformance.createStartingMarker(`${action}:user-action`);
    this.#disableCartItems();

    const cartTotal = this.refs.cartTotal;

    // Collect all cart sections that need updating
    const sectionsToUpdate = new Set([this.sectionId]);
    document.querySelectorAll('cart-items-component').forEach(el => {
      if (el.dataset.sectionId) sectionsToUpdate.add(el.dataset.sectionId);
    });

    const body = JSON.stringify({
      line,
      quantity,
      sections: [...sectionsToUpdate].join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }))
      .then(r => r.text())
      .then(text => {
        const parsed = JSON.parse(text);
        resetShimmer(this);

        if (parsed.errors) return this.#handleCartError(line, parsed);

        // Update quantity selectors
        this.#updateQuantitySelectors(parsed);

        // Update cart count + sections
        this.dispatchEvent(
          new CartUpdateEvent(
            {},
            this.sectionId,
            { sections: parsed.sections }
          )
        );

        morphSection(this.sectionId, parsed.sections[this.sectionId]);
        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch(console.error)
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(marker);
      });
  }


  /* ================= ERROR HANDLING ================= */

  // Handle quantity update errors
  #handleCartError(line, parsed) {
    const selector = this.refs.quantitySelectors[line - 1];
    const input = selector?.querySelector('input');
    if (!input) return;

    input.value = input.defaultValue;

    const errorText = this.refs[`cartItemError-${line}`];
    const errorBox = this.refs[`cartItemErrorContainer-${line}`];

    errorText.textContent = parsed.errors;
    errorBox.classList.remove('hidden');
  }


  /* ================= CART UPDATE EVENTS ================= */

  // React to cart updates from other components
  #handleCartUpdate = (event) => {
    if (event.target === this) return;

    const html = event.detail.data.sections?.[this.sectionId];
    if (html) {
      morphSection(this.sectionId, html);
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };


  /* ================= UI HELPERS ================= */

  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  // Sync quantity inputs with cart data
  #updateQuantitySelectors(updated) {
    if (!updated.items) return;

    updated.items.forEach(item => {
      const selectors = document.querySelectorAll(
        `[data-variant-id="${item.variant_id}"]`
      );

      selectors.forEach(sel => {
        const input = sel.querySelector('input[data-cart-quantity]');
        if (!input) return;
        input.setAttribute('data-cart-quantity', item.quantity);
        sel.updateCartQuantity?.();
      });
    });
  }

  // Update + / - button state
  #updateCartQuantitySelectorButtonStates() {
    document.querySelectorAll('cart-quantity-selector-component')
      .forEach(sel => sel.updateButtonStates?.());
  }

  
  /* ================= SECTION ID ================= */

  get sectionId() {
    const id = this.dataset.sectionId;
    if (!id) throw new Error('Section ID missing');
    return id;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
